import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription, EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Chart } from 'chart.js';
import moment from 'moment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { openWindowAndDownloadWithFilename, PortalHotToastService, setupUntilDestroy } from '@portal-workspace/grow-ui-library';
import {
  CONSENT_DASHBOARD_GRANULARITIES,
  ConsentDashboardAnalytics,
  ConsentDashboardGranularity,
  ConsentDashboardMetrics,
  ConsentIndividualOutcome,
} from '@portal-workspace/grow-shared-library';
import { ConsentDashboardService } from '../../service/consent-dashboard.service';

const OUTCOME_LABELS: Record<ConsentIndividualOutcome, string> = {
  signed: 'Signed',
  pending: 'Pending',
  declined: 'Declined',
  expired: 'Expired',
  invalidated: 'Invalidated',
};

const OUTCOME_COLORS: Record<ConsentIndividualOutcome, string> = {
  signed: '#66bb6a',
  pending: '#42a5f5',
  declined: '#ef5350',
  expired: '#ffa726',
  invalidated: '#8d6e63',
};

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './consent-dashboard.page.html',
  styleUrls: ['./consent-dashboard.page.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, DecimalPipe, TitleCasePipe, MatCardModule, MatButtonModule, MatIconModule, MatTooltipModule],
  providers: [ConsentDashboardService],
})
export class ConsentDashboardPage implements OnInit, AfterViewInit {

  subscriptions: Subscription[] = [];

  formControlFrom = new FormControl<string>('', { nonNullable: true });
  formControlTo = new FormControl<string>('', { nonNullable: true });
  formControlGranularity = new FormControl<ConsentDashboardGranularity>('week', { nonNullable: true });
  granularities = CONSENT_DASHBOARD_GRANULARITIES;

  analytics: ConsentDashboardAnalytics | null = null;
  metrics: ConsentDashboardMetrics | null = null;

  private viewReady = false;

  @ViewChild('volumeChart') volumeChartRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('durationChart') durationChartRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('statusChart') statusChartRef?: ElementRef<HTMLCanvasElement>;

  // Chart.js instances are held untyped (matching the other dashboard pages) so the specific
  // per-type generics (bar/line/doughnut) do not fight the shared field type.
  private volumeChart: any;
  private durationChart: any;
  private statusChart: any;

  constructor(private consentDashboardService: ConsentDashboardService,
              private toastService: PortalHotToastService) {
    this.formControlFrom.setValue(moment().subtract(90, 'days').format('YYYY-MM-DD'));
    this.formControlTo.setValue(moment().format('YYYY-MM-DD'));
  }

  ngOnInit() {
    setupUntilDestroy(this);
    this.reload();
    this.subscriptions.push(this.formControlGranularity.valueChanges.subscribe(() => this.reload()));
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.renderCharts();
  }

  get from(): string { return this.formControlFrom.value; }
  get to(): string { return this.formControlTo.value; }
  get granularity(): ConsentDashboardGranularity { return this.formControlGranularity.value; }

  get hasData(): boolean { return !!this.metrics && this.metrics.totalEnvelopes > 0; }

  applyRange() {
    this.reload();
  }

  reload() {
    if (!this.from || !this.to) {
      return;
    }
    this.subscriptions.push(this.consentDashboardService.getAnalytics(this.from, this.to, this.granularity).pipe(
      this.toastService.spinnerObservable(),
      tap(response => {
        this.analytics = response.payload;
        this.metrics = response.payload.metrics;
        this.renderCharts();
      }),
      catchError(() => {
        this.toastService.error('Unable to load consent dashboard analytics');
        return EMPTY;
      }),
    ).subscribe());
  }

  downloadCsv() {
    if (!this.from || !this.to) {
      return;
    }
    this.subscriptions.push(this.consentDashboardService.downloadCsv(this.from, this.to, this.granularity).pipe(
      this.toastService.spinnerObservable(),
      tap(blob => openWindowAndDownloadWithFilename(`consent-dashboard-${this.from}-to-${this.to}.csv`, blob)),
      catchError(() => {
        this.toastService.error('Unable to download consent dashboard CSV');
        return EMPTY;
      }),
    ).subscribe());
  }

  formatHours(hours: number | null | undefined): string {
    if (hours === null || hours === undefined) {
      return '\u2014';
    }
    if (hours >= 48) {
      return `${(hours / 24).toFixed(1)} d`;
    }
    return `${hours.toFixed(1)} h`;
  }

  private renderCharts() {
    if (!this.viewReady || !this.analytics) {
      return;
    }
    this.renderVolumeChart();
    this.renderDurationChart();
    this.renderStatusChart();
  }

  private renderVolumeChart() {
    const canvas = this.volumeChartRef?.nativeElement;
    if (!canvas || !this.analytics) {
      return;
    }
    const points = this.analytics.timeseries;
    this.volumeChart?.destroy();
    this.volumeChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: points.map(p => p.bucketStart),
        datasets: [
          { label: 'Entered', data: points.map(p => p.entered), backgroundColor: '#90a4ae' },
          { label: 'Signed', data: points.map(p => p.signed), backgroundColor: '#66bb6a' },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
        plugins: { legend: { position: 'bottom' } },
      },
    });
  }

  private renderDurationChart() {
    const canvas = this.durationChartRef?.nativeElement;
    if (!canvas || !this.analytics) {
      return;
    }
    const points = this.analytics.timeseries;
    this.durationChart?.destroy();
    this.durationChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: points.map(p => p.bucketStart),
        datasets: [
          {
            label: 'Avg hours to sign',
            data: points.map(p => p.avgHoursToSign),
            borderColor: '#5d40ff',
            backgroundColor: 'rgba(93, 64, 255, 0.15)',
            spanGaps: true,
            tension: 0.3,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { y: { beginAtZero: true } },
        plugins: { legend: { position: 'bottom' } },
      },
    });
  }

  private renderStatusChart() {
    const canvas = this.statusChartRef?.nativeElement;
    if (!canvas || !this.analytics) {
      return;
    }
    const breakdown = this.analytics.statusBreakdown;
    this.statusChart?.destroy();
    this.statusChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: breakdown.map(b => OUTCOME_LABELS[b.outcome] ?? b.outcome),
        datasets: [
          {
            data: breakdown.map(b => b.count),
            backgroundColor: breakdown.map(b => OUTCOME_COLORS[b.outcome] ?? '#bdbdbd'),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'right' } },
      },
    });
  }
}
